import React, { useState, useMemo, useRef, useEffect } from 'react';
import { ChevronDown, ChevronUp, Package, Activity, ChevronLeft, ChevronRight, Search, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { BoardChannel, BoardEntity } from '../../../../../stores/boardStore';
import { SafeImage } from '../../../../../components/common/SafeImage';

interface ProductExplorerProps {
    channels: BoardChannel[];
    entities: BoardEntity[];
    onProductSelect: (product: BoardEntity | null) => void;
    selectedProductId: string | null;
    renderProductMetric?: (product: BoardEntity) => React.ReactNode;
}

const getProductImage = (entity: BoardEntity) => {
    if (!entity.feed?.assets) return null;
    const { assets } = entity.feed;
    if (assets.low && assets.low.length > 0) return assets.low[0];
    if (assets.high && assets.high.length > 0) return assets.high[0];
    return null;
};

const ChannelGroup: React.FC<{
    channel: BoardChannel;
    products: BoardEntity[];
    isExpanded: boolean;
    onToggle: () => void;
    onProductSelect: (product: BoardEntity) => void;
    selectedProductId: string | null;
    renderProductMetric?: (product: BoardEntity) => React.ReactNode;
}> = ({ channel, products, isExpanded, onToggle, onProductSelect, selectedProductId, renderProductMetric }) => {
    const { t } = useTranslation();
    const scrollRef = useRef<HTMLDivElement>(null);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    const filteredProducts = useMemo(() => {
        if (!searchQuery.trim()) return products;
        const q = searchQuery.toLowerCase().trim();
        return products.filter(p => p.name?.toLowerCase().includes(q));
    }, [products, searchQuery]);

    const checkScroll = () => {
        if (scrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
            setShowLeftArrow(scrollLeft > 10);
            setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
        }
    };

    useEffect(() => {
        if (isExpanded) {
            // Tiny delay to ensure DOM is rendered
            const timer = setTimeout(checkScroll, 100);
            return () => clearTimeout(timer);
        }
    }, [isExpanded, filteredProducts]);

    const scroll = (direction: 'left' | 'right') => {
        if (scrollRef.current) {
            const { clientWidth } = scrollRef.current;
            const scrollAmount = direction === 'left' ? -clientWidth * 0.8 : clientWidth * 0.8;
            scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        }
    };

    return (
        <motion.div
            layout
            className="bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-800 shadow-sm overflow-hidden"
        >
            <div
                className="p-3 sm:p-4 flex items-center justify-between cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors gap-2 min-w-0"
                onClick={onToggle}
            >
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-neutral-100 dark:bg-neutral-800 flex-shrink-0 flex items-center justify-center border border-neutral-200 dark:border-neutral-700 overflow-hidden">
                        {(() => {
                            if (channel.icon && (channel.icon.startsWith("http") || channel.icon.startsWith("/"))) {
                                return <SafeImage src={channel.icon} alt={channel.name} className="w-4 h-4 sm:w-5 sm:h-5 object-contain" />;
                            }
                            if (channel.channelTypeConfig?.icon && (channel.channelTypeConfig.icon.startsWith("/") || channel.channelTypeConfig.icon.startsWith("http"))) {
                                return <SafeImage src={channel.channelTypeConfig.icon} alt={channel.name} className="w-4 h-4 sm:w-5 sm:h-5 object-contain" />;
                            }
                            if (channel.icon) {
                                return <span className="text-xs sm:text-sm">{channel.icon}</span>;
                            }
                            return <span className="text-[10px] font-bold text-neutral-500 uppercase">{channel.name.substring(0, 1)}</span>;
                        })()}
                    </div>
                    <div className="min-w-0">
                        <h4 className="font-bold text-sm text-neutral-800 dark:text-neutral-200 truncate">{channel.name}</h4>
                        <p className="text-[9px] sm:text-[10px] text-neutral-400 uppercase tracking-wider font-semibold truncate">
                            {t('common.productsCount', { count: products.length, defaultValue: '{{count}} Products' })}
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2 sm:gap-4 min-w-0 shrink-0">
                    <AnimatePresence mode="wait">
                        {isExpanded ? (
                            <motion.div
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 20 }}
                                className="relative flex-1 max-w-[120px] sm:max-w-[200px]"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-neutral-400" />
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder={t('actions.search', 'Search...') as string}
                                    className="w-full bg-neutral-50 dark:bg-neutral-800 border-none rounded-lg pl-6 pr-2 py-1 text-xs focus:ring-1 focus:ring-accent-primary transition-all"
                                />
                                {searchQuery && (
                                    <button
                                        onClick={() => setSearchQuery('')}
                                        className="absolute right-1 top-1/2 -translate-y-1/2"
                                    >
                                        <X className="w-3 h-3 text-neutral-400 hover:text-neutral-600" />
                                    </button>
                                )}
                            </motion.div>
                        ) : (
                            <motion.div
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                className="flex items-center gap-1 sm:gap-1.5 overflow-hidden flex-nowrap"
                            >
                                {products.slice(0, 8).map((p, i) => {
                                    const img = getProductImage(p);
                                    const isSelected = selectedProductId === p.id;
                                    return (
                                        <div
                                            key={p.id}
                                            className={`flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-md border overflow-hidden transition-all ${i >= 3 ? 'hidden sm:block' : ''} ${isSelected ? 'border-accent-primary ring-1 ring-accent-primary/50' : 'border-neutral-200 dark:border-neutral-800'
                                                }`}
                                        >
                                            {img ? (
                                                <img src={img} alt="" className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center">
                                                    <Package className="w-2.5 h-2.5 text-neutral-300" />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                {products.length > 3 && (
                                    <div className="text-[9px] font-bold text-neutral-400 dark:text-neutral-500 whitespace-nowrap ml-0.5 sm:hidden">
                                        +{products.length - 3}
                                    </div>
                                )}
                                {products.length > 8 && (
                                    <div className="hidden sm:block text-[9px] font-bold text-neutral-400 dark:text-neutral-500 whitespace-nowrap ml-1">
                                        +{products.length - 8}
                                    </div>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>
                    <div className="shrink-0 flex items-center">
                        {isExpanded ? <ChevronUp className="w-4 h-4 text-neutral-400" /> : <ChevronDown className="w-4 h-4 text-neutral-400" />}
                    </div>
                </div>
            </div>

            <AnimatePresence>
                {isExpanded && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                    >
                        <div className="p-4 pt-0 border-t border-neutral-100 dark:border-neutral-800 relative group/scroll">
                            <div className="relative mt-4">
                                {/* Navigation Arrows */}
                                <AnimatePresence>
                                    {showLeftArrow && (
                                        <motion.button
                                            initial={{ opacity: 0, x: -10 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: -10 }}
                                            onClick={(e) => { e.stopPropagation(); scroll('left'); }}
                                            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-1.5 rounded-full bg-white/90 dark:bg-neutral-800/90 shadow-lg border border-neutral-100 dark:border-neutral-700 text-neutral-600 dark:text-neutral-300 hover:bg-white dark:hover:bg-neutral-800 transition-colors"
                                        >
                                            <ChevronLeft className="w-4 h-4" />
                                        </motion.button>
                                    )}
                                    {showRightArrow && (
                                        <motion.button
                                            initial={{ opacity: 0, x: 10 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: 10 }}
                                            onClick={(e) => { e.stopPropagation(); scroll('right'); }}
                                            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-1.5 rounded-full bg-white/90 dark:bg-neutral-800/90 shadow-lg border border-neutral-100 dark:border-neutral-700 text-neutral-600 dark:text-neutral-300 hover:bg-white dark:hover:bg-neutral-800 transition-colors"
                                        >
                                            <ChevronRight className="w-4 h-4" />
                                        </motion.button>
                                    )}
                                </AnimatePresence>

                                <div
                                    ref={scrollRef}
                                    onScroll={checkScroll}
                                    className="grid grid-rows-2 grid-flow-col gap-3 overflow-x-auto no-scrollbar p-1.5 -m-1.5 h-[346px] relative"
                                    style={{
                                        gridAutoColumns: '140px',
                                        gridTemplateRows: 'repeat(2, 162px)',
                                        scrollbarWidth: 'none',
                                    }}
                                >
                                    <AnimatePresence mode="popLayout">
                                        {filteredProducts.length > 0 ? (
                                            filteredProducts.map(product => {
                                                const img = getProductImage(product);
                                                const isSelected = selectedProductId === product.id;
                                                return (
                                                    <motion.button
                                                        layout
                                                        initial={{ opacity: 0, scale: 0.9 }}
                                                        animate={{ opacity: 1, scale: 1 }}
                                                        exit={{ opacity: 0, scale: 0.9 }}
                                                        key={product.id}
                                                        onClick={() => onProductSelect(product)}
                                                        className={`group relative flex flex-col items-center p-2 rounded-xl border transition-colors duration-200 w-[140px] h-[162px] ${isSelected
                                                            ? 'bg-accent-primary/5 border-accent-primary/30 ring-1 ring-accent-primary/20 shadow-sm'
                                                            : 'bg-white dark:bg-neutral-900 border-neutral-100 dark:border-neutral-800 hover:border-accent-primary/20 hover:bg-neutral-50 dark:hover:bg-neutral-800/50'
                                                            }`}
                                                    >
                                                        <div className="relative w-full aspect-square rounded-lg bg-neutral-50 dark:bg-neutral-800 overflow-hidden mb-2 pointer-events-none">
                                                            {img ? (
                                                                <img src={img} alt={product.name} className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                                                            ) : (
                                                                <div className="w-full h-full flex items-center justify-center text-neutral-300">
                                                                    <Package className="w-8 h-8 opacity-20" />
                                                                </div>
                                                            )}
                                                            {isSelected && (
                                                                <div className="absolute inset-0 bg-accent-primary/10 flex items-center justify-center">
                                                                    <Activity className="w-6 h-6 text-accent-primary animate-pulse" />
                                                                </div>
                                                            )}
                                                            
                                                            {/* Generic Overlay Metric */}
                                                            {renderProductMetric && (
                                                                <div className="absolute bottom-0 left-0 right-0 p-1 bg-gradient-to-t from-black/60 to-transparent flex justify-center">
                                                                    {renderProductMetric(product)}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <span className="w-full text-[11px] font-bold text-center text-neutral-700 dark:text-neutral-300 truncate px-1">
                                                            {product.name}
                                                        </span>
                                                        {isSelected && (
                                                            <motion.div
                                                                layoutId="active-indicator"
                                                                className="absolute top-1 right-1 w-3 h-3 bg-accent-primary rounded-full border-2 border-white dark:border-neutral-900 shadow-sm"
                                                            />
                                                        )}
                                                    </motion.button>
                                                );
                                            })
                                        ) : (
                                            <motion.div
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                className="flex flex-col items-center justify-center w-full col-span-full h-full text-neutral-400 gap-2 absolute inset-0 pointer-events-none"
                                            >
                                                <Package className="w-8 h-8 opacity-20" />
                                                <p className="text-[10px] font-bold uppercase tracking-widest opacity-40">
                                                    {t('common.noProductsFound', 'No products found')}
                                                </p>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

export const ProductExplorer: React.FC<ProductExplorerProps> = ({
    channels,
    entities,
    onProductSelect,
    selectedProductId,
    renderProductMetric
}) => {
    // 1. Identify Products Grouped by Channel
    const productsByChannel = useMemo(() => {
        const groups: Record<string, { channel: BoardChannel; products: BoardEntity[] }> = {};

        entities.forEach(entity => {
            const type = (entity.entityType || '').toLowerCase();

            // Exclude paid marketing hierarchy: ads, ad sets, and campaigns
            const isPaidEntity = type.includes('ad') ||
                type.includes('set') ||
                type.includes('campaign');

            if (isPaidEntity) return;

            // We consider 'product' or 'variant' as products. 
            // Also, entities that have images are likely products in an ecommerce context.
            const isProduct = type.includes('product') ||
                type.includes('variant') ||
                (entity.feed?.assets && (entity.feed.assets.high?.length > 0 || entity.feed.assets.low?.length > 0));

            if (isProduct) {
                if (!groups[entity.channelId]) {
                    const channel = channels.find(c => c.id === entity.channelId);
                    if (channel) {
                        groups[entity.channelId] = { channel, products: [] };
                    }
                }
                if (groups[entity.channelId]) {
                    groups[entity.channelId].products.push(entity);
                }
            }
        });

        return Object.values(groups).sort((a, b) => b.products.length - a.products.length);
    }, [entities, channels]);

    const [expandedChannels, setExpandedChannels] = useState<Record<string, boolean>>(() => {
        if (productsByChannel.length > 0) {
            return { [productsByChannel[0].channel.id]: true };
        }
        return {};
    });

    const toggleChannel = (channelId: string) => {
        setExpandedChannels(prev => ({ ...prev, [channelId]: !prev[channelId] }));
    };

    // Ensure first group is expanded by default when data arrives
    useEffect(() => {
        if (productsByChannel.length > 0 && Object.keys(expandedChannels).length === 0) {
            setExpandedChannels({ [productsByChannel[0].channel.id]: true });
        }
    }, [productsByChannel]);

    if (productsByChannel.length === 0) return null;

    return (
        <div className="space-y-3">
            {productsByChannel.map(({ channel, products }) => (
                <ChannelGroup
                    key={channel.id}
                    channel={channel}
                    products={products}
                    isExpanded={!!expandedChannels[channel.id]}
                    onToggle={() => toggleChannel(channel.id)}
                    onProductSelect={onProductSelect}
                    selectedProductId={selectedProductId}
                    renderProductMetric={renderProductMetric}
                />
            ))}
        </div>
    );
};
